// src/services/leanFlowEngine.js
// Motor de reglas para flujo Lean Validation
// Ticket 1: Motor de flujo y reglas de habilitación (Rules Engine)

import leanFlowConfig from '../config/lean_flow.v1.json';

class LeanFlowEngine {
  constructor(config = leanFlowConfig) {
    this.config = config;
    this.stages = config.stages;
    this.allowedNext = config.allowedNext;
    this.strictFlow = config.strictFlow;
    
    // Estado interno del motor
    this.state = {
      selections: [], // Array de {stepIndex, stageId, locked}
      currentStepIndex: 0,
      completedPath: []
    };
  }

  /**
   * Obtiene todos los stages disponibles
   */
  getAllStages() {
    return this.stages.map(s => ({
      id: s.id,
      label: s.label,
      description: s.description
    }));
  }

  /**
   * Obtiene el contenido completo de un stage
   */
  getStageContent(stageId) {
    const stage = this.stages.find(s => s.id === stageId);
    if (!stage) {
      throw new Error(`Stage ${stageId} no encontrado`);
    }
    return {
      stageId: stage.id, // Incluir explícitamente el stageId
      ...stage,
      content: stage.content || {}
    };
  }

  /**
   * Obtiene los stages permitidos después de un stage dado
   */
  getAllowedNext(stageId) {
    if (!stageId) return [];
    
    const allowed = this.allowedNext[stageId] || [];
    return allowed.map(id => {
      const stage = this.stages.find(s => s.id === id);
      return stage ? { id: stage.id, label: stage.label } : null;
    }).filter(Boolean);
  }

  /**
   * Establece una selección en un paso (sin fijar)
   */
  setSelection(stepIndex, stageId) {
    // Validar que el stage existe
    const stage = this.stages.find(s => s.id === stageId);
    if (!stage) {
      throw new Error(`Stage ${stageId} inválido`);
    }

    // Si hay selecciones posteriores, resetearlas
    if (stepIndex < this.state.selections.length - 1) {
      this.resetFrom(stepIndex + 1);
    }

    // Actualizar o agregar selección
    const existingIndex = this.state.selections.findIndex(s => s.stepIndex === stepIndex);
    
    if (existingIndex >= 0) {
      this.state.selections[existingIndex] = {
        stepIndex,
        stageId,
        locked: false
      };
    } else {
      this.state.selections.push({
        stepIndex,
        stageId,
        locked: false
      });
    }

    this.state.currentStepIndex = stepIndex;
    
    return this.getState();
  }

  /**
   * Fija (lock) una selección en un paso específico
   * Esto habilita el siguiente combo
   */
  lockStep(stepIndex, stageId) {
    const selection = this.state.selections.find(s => s.stepIndex === stepIndex);
    
    if (!selection) {
      throw new Error(`No hay selección en el paso ${stepIndex} para fijar`);
    }

    if (selection.stageId !== stageId) {
      throw new Error(`El stage ${stageId} no coincide con la selección actual`);
    }

    // Marcar como fijado
    selection.locked = true;

    // Agregar a ruta completada
    if (!this.state.completedPath.includes(stageId)) {
      this.state.completedPath.push(stageId);
    }

    // Habilitar siguiente paso
    this.state.currentStepIndex = stepIndex + 1;

    return this.getState();
  }

  /**
   * Resetea todos los pasos desde stepIndex hacia adelante
   */
  resetFrom(stepIndex) {
    // Filtrar selecciones, mantener solo las anteriores
    this.state.selections = this.state.selections.filter(s => s.stepIndex < stepIndex);
    
    // Actualizar ruta completada
    this.state.completedPath = this.state.selections
      .filter(s => s.locked)
      .map(s => s.stageId);
    
    // Ajustar índice actual
    if (this.state.currentStepIndex > stepIndex) {
      this.state.currentStepIndex = stepIndex;
    }

    return this.getState();
  }

  /**
   * Verifica si un paso está habilitado
   */
  isStepEnabled(stepIndex) {
    if (stepIndex === 0) return true; // El primer paso siempre está habilitado
    
    // Un paso está habilitado si el anterior está fijado
    const previousStep = this.state.selections.find(s => s.stepIndex === stepIndex - 1);
    return previousStep && previousStep.locked;
  }

  /**
   * Verifica si el flujo está completo
   */
  isFlowComplete() {
    const lastStage = this.stages[this.stages.length - 1];
    const lastSelection = this.state.selections.find(s => s.stageId === lastStage.id);
    return lastSelection && lastSelection.locked;
  }

  /**
   * Obtiene las opciones disponibles para un paso específico
   */
  getOptionsForStep(stepIndex) {
    if (stepIndex === 0) {
      // El primer paso puede ser cualquier stage inicial
      return this.getAllStages();
    }

    const previousStep = this.state.selections.find(s => s.stepIndex === stepIndex - 1);
    if (!previousStep || !previousStep.locked) {
      return []; // No hay opciones si el paso anterior no está fijado
    }

    return this.getAllowedNext(previousStep.stageId);
  }

  /**
   * Obtiene el contenido del panel actual según la última selección
   */
  getCurrentPanel() {
    if (this.state.selections.length === 0) {
      return null;
    }

    const currentSelection = this.state.selections[this.state.selections.length - 1];
    return this.getStageContent(currentSelection.stageId);
  }

  /**
   * Obtiene el estado completo del motor
   */
  getState() {
    return {
      selections: [...this.state.selections],
      currentStepIndex: this.state.currentStepIndex,
      completedPath: [...this.state.completedPath],
      isComplete: this.isFlowComplete(),
      currentPanel: this.getCurrentPanel()
    };
  }

  /**
   * Reinicia el motor completamente
   */
  reset() {
    this.state = {
      selections: [],
      currentStepIndex: 0,
      completedPath: []
    };
    return this.getState();
  }

  /**
   * Obtiene información de un paso específico
   */
  getStepInfo(stepIndex) {
    const selection = this.state.selections.find(s => s.stepIndex === stepIndex);
    const enabled = this.isStepEnabled(stepIndex);
    const options = this.getOptionsForStep(stepIndex);

    return {
      stepIndex,
      enabled,
      selection: selection || null,
      options,
      locked: selection ? selection.locked : false
    };
  }

  /**
   * Exporta el estado actual para guardarlo
   */
  exportState() {
    return JSON.stringify(this.state, null, 2);
  }

  /**
   * Importa un estado previamente exportado
   */
  importState(stateJson) {
    try {
      const imported = JSON.parse(stateJson);
      this.state = {
        selections: imported.selections || [],
        currentStepIndex: imported.currentStepIndex || 0,
        completedPath: imported.completedPath || []
      };
      return this.getState();
    } catch (error) {
      throw new Error('Estado inválido para importar');
    }
  }
}

// Instancia singleton para uso en la aplicación
export const leanFlowEngine = new LeanFlowEngine();

// Exportar también la clase para testing
export default LeanFlowEngine;
